import AnnotationToolbar from "./AnnotationToolbar";
import DocumentPreviewCanvas from "./DocumentPreviewCanvas";
import VariablePalette from "./VariablePalette";
import { ANNOTATION_TOOLS } from "./constants";
import { CommentDialog, ConditionDialog, NewVariableDialog } from "./dialogs";
import {
  BorderColorRounded as DrawIcon,
  CloudDoneRounded as CloudDoneIcon,
  CloudUploadRounded as UploadIcon,
  DescriptionRounded as DocIcon,
  DownloadRounded as DownloadIcon,
  ErrorOutlineRounded as ErrorIcon,
  PriorityHighRounded as PriorityIcon,
  SendRounded as SubmitIcon,
  SwapHorizRounded as ReplaceIcon,
  SyncRounded as SyncIcon,
  ZoomInRounded as ZoomInIcon,
  ZoomOutRounded as ZoomOutIcon,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Chip,
  Divider,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import Button from "components/common/Button";
import { createFileEntry, getDownloadUrl } from "fileUtils";
import { useRef, useState, useCallback, useEffect } from "react";

const DocumentVariableMapper = ({
  onSubmitRequest,
  requestType = "NEW_TEMPLATE",
  initialFile,
  initialPlacements = null,
  initialAnnotations = null,
  initialCustomFields = null,
  initialMetadata = null,
  // When true, docx previews are rendered server-side via LibreOffice so
  // the canvas matches the fidelity of the system's published PDFs. The
  // mutation hook does the HTTP call; passed through so this component
  // stays reusable in tests without touching the redux store.
  useServerSidePdfPreview = false,
  convertFileToPdfFn = null,
  // Original template file_id, preserved across "Replace template file" so
  // the Download Original button always returns the system template even
  // after the user has uploaded their edited version on top.
  originalS3FileId = null,
  originalFileName = null,
  submitLabel,
}) => {
  const theme = useTheme();
  const fileInputRef = useRef(null);
  const [uploadedFile, setUploadedFile] = useState(
    initialFile ? initialFile.file : null
  );
  const [s3FileId, setS3FileId] = useState(
    initialFile ? initialFile.s3FileId : null
  );
  const [uploadStatus, setUploadStatus] = useState(
    initialFile ? "uploaded" : "idle"
  );
  const initialS3FileIdRef = useRef(initialFile ? initialFile.s3FileId : null);
  const fileReplaced =
    initialS3FileIdRef.current != null &&
    s3FileId != null &&
    s3FileId !== initialS3FileIdRef.current;

  const [searchTerm, setSearchTerm] = useState("");
  const [placements, setPlacements] = useState(initialPlacements ?? []);
  const [zoom, setZoom] = useState(0.8);
  const [newVariableDialogOpen, setNewVariableDialogOpen] = useState(false);
  const [requestedVariables, setRequestedVariables] = useState(
    initialCustomFields ?? []
  );

  const [annotations, setAnnotations] = useState(initialAnnotations ?? []);

  // Inline submit form state. Previously these lived in SubmitRequestDialog
  // (which is still used by BulkTemplateReview). Re-seed when initialMetadata
  // changes so the "Continue Editing" flow pre-populates correctly.
  const [requestTitle, setRequestTitle] = useState(
    initialMetadata?.title ?? ""
  );
  const [requestDescription, setRequestDescription] = useState(
    initialMetadata?.description ?? ""
  );
  const [requestPriority, setRequestPriority] = useState(
    initialMetadata?.priority ?? "MEDIUM"
  );
  useEffect(() => {
    if (initialMetadata) {
      setRequestTitle(initialMetadata.title ?? "");
      setRequestDescription(initialMetadata.description ?? "");
      setRequestPriority(initialMetadata.priority ?? "MEDIUM");
    }
  }, [initialMetadata]);

  const prevInitialFileRef = useRef(initialFile);
  useEffect(() => {
    if (initialFile && initialFile !== prevInitialFileRef.current) {
      setUploadedFile(initialFile.file);
      setS3FileId(initialFile.s3FileId);
      setUploadStatus("uploaded");
      initialS3FileIdRef.current = initialFile.s3FileId;
      setPlacements(initialPlacements ?? []);
      setAnnotations(initialAnnotations ?? []);
      setRequestedVariables(initialCustomFields ?? []);
    }
    prevInitialFileRef.current = initialFile;
  }, [initialFile, initialPlacements, initialAnnotations, initialCustomFields]);
  const [commentDialogOpen, setCommentDialogOpen] = useState(false);
  const [pendingCommentPosition, setPendingCommentPosition] = useState(null);

  const [conditionDialogOpen, setConditionDialogOpen] = useState(false);
  const [editingPlacement, setEditingPlacement] = useState(null);

  const isNewTemplate = requestType === "NEW_TEMPLATE";
  const [isDragging, setIsDragging] = useState(false);

  const handleFileSelect = async (e) => {
    const file = e.target?.files?.[0] || e.dataTransfer?.files?.[0];
    if (file) {
      setUploadedFile(file);
      setPlacements([]);
      setS3FileId(null);
      setUploadStatus("uploading");

      try {
        const fileId = await createFileEntry(file, false);
        setS3FileId(fileId);
        setUploadStatus("uploaded");
      } catch {
        setUploadStatus("error");
      }
    }
    e.target.value = null;
  };

  const handleAddPlacement = useCallback((placement) => {
    setPlacements((prev) => [...prev, placement]);
  }, []);

  const handleRemovePlacement = useCallback((id) => {
    setPlacements((prev) => prev.filter((p) => p.id !== id));
  }, []);

  const handleMovePlacement = useCallback((id, newPosition) => {
    setPlacements((prev) =>
      prev.map((p) =>
        p.id === id ? { ...p, x: newPosition.x, y: newPosition.y } : p
      )
    );
  }, []);

  const handleEditCondition = useCallback((placement) => {
    setEditingPlacement(placement);
    setConditionDialogOpen(true);
  }, []);

  const handleUpdateCondition = useCallback((id, condition) => {
    setPlacements((prev) =>
      prev.map((p) => (p.id === id ? { ...p, condition } : p))
    );
  }, []);

  const handleZoomIn = () => setZoom((prev) => Math.min(1.5, prev + 0.1));
  const handleZoomOut = () => setZoom((prev) => Math.max(0.5, prev - 0.1));

  // Download Original pulls the picked template's system file so the user can
  // edit it offline in Word. Distinct from the live `s3FileId` because the
  // user may have already replaced the loaded template with their edit; we
  // always want the system-of-record original here.
  const [downloadingOriginal, setDownloadingOriginal] = useState(false);
  const handleDownloadOriginal = useCallback(async () => {
    if (!originalS3FileId) return;
    setDownloadingOriginal(true);
    try {
      const url = await getDownloadUrl(originalS3FileId);
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Failed to download original (${response.status})`);
      }
      const blob = await response.blob();
      const filename =
        originalFileName || `${initialMetadata?.title ?? "template"}.docx`;
      const anchorUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = anchorUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(anchorUrl);
    } finally {
      setDownloadingOriginal(false);
    }
  }, [originalS3FileId, originalFileName, initialMetadata]);

  const handleAddAnnotation = useCallback((annotation) => {
    setAnnotations((prev) => [...prev, annotation]);
  }, []);

  const handleRemoveAnnotation = useCallback((id) => {
    setAnnotations((prev) => prev.filter((a) => a.id !== id));
  }, []);

  const handleMoveAnnotation = useCallback((id, newPosition) => {
    setAnnotations((prev) =>
      prev.map((a) =>
        a.id === id ? { ...a, x: newPosition.x, y: newPosition.y } : a
      )
    );
  }, []);

  const handleResizeAnnotation = useCallback((id, newSize) => {
    setAnnotations((prev) =>
      prev.map((a) =>
        a.id === id ? { ...a, width: newSize.width, height: newSize.height } : a
      )
    );
  }, []);

  const handleRequestComment = useCallback((position) => {
    setPendingCommentPosition(position);
    setCommentDialogOpen(true);
  }, []);

  const handleAddComment = useCallback(
    (text) => {
      if (pendingCommentPosition) {
        handleAddAnnotation({
          id: `annotation-${Date.now()}`,
          type: "comment",
          x: pendingCommentPosition.x,
          y: pendingCommentPosition.y,
          text,
          page: pendingCommentPosition.page ?? 1,
          color: "#8e24aa",
        });
        setPendingCommentPosition(null);
      }
    },
    [pendingCommentPosition, handleAddAnnotation]
  );

  const handleRequestNewVariable = (data) => {
    const newVariable = {
      id: Date.now(),
      path: `custom.${data.fieldName
        .toLowerCase()
        .replace(/\s+/g, "_")}_${Date.now()}`,
      label: data.fieldName,
      type: data.dataType,
      description: data.description,
      dataSource: data.dataSource,
      captureMethod: data.captureMethod,
      isCustom: true,
    };
    setRequestedVariables((prev) => [...prev, newVariable]);
  };

  const placedVariables = placements.map((p) => p.variable);

  const canSubmit =
    placements.length > 0 || annotations.length > 0 || fileReplaced;

  const handleInlineSubmit = () => {
    onSubmitRequest?.({
      title:
        requestTitle ||
        `${isNewTemplate ? "New Template" : "Template Edit"}: ${
          uploadedFile?.name ?? ""
        }`,
      description:
        requestDescription ||
        `${
          isNewTemplate
            ? "New template request"
            : "Template modification request"
        } with ${placements.length} field placements and ${
          annotations.length
        } annotations.`,
      priority: requestPriority,
      fileName: uploadedFile?.name ?? "",
      s3FileId,
      fileType:
        uploadedFile?.type ||
        (uploadedFile?.name?.endsWith?.(".pdf")
          ? "application/pdf"
          : "application/vnd.openxmlformats-officedocument.wordprocessingml.document"),
      placements,
      customFields: requestedVariables,
      annotations,
    });
    if (!initialFile) {
      setUploadedFile(null);
      setS3FileId(null);
      setUploadStatus("idle");
      setPlacements([]);
      setRequestedVariables([]);
      setAnnotations([]);
      setRequestTitle("");
      setRequestDescription("");
      setRequestPriority("MEDIUM");
    }
  };

  if (!uploadedFile) {
    return (
      <Box>
        <Alert severity={"info"} sx={{ mb: 2 }}>
          {isNewTemplate
            ? "Upload the Word document (.docx) you want to use as a new template. You can then drag and drop data fields onto the document to show where they should appear."
            : "Upload the existing template (.docx or .pdf) you want to modify. You can then add annotations and data field placements to show what changes are needed."}
        </Alert>

        <Paper
          variant={"outlined"}
          onClick={() => fileInputRef.current?.click()}
          onDrop={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setIsDragging(false);
            handleFileSelect(e);
          }}
          onDragOver={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setIsDragging(true);
          }}
          onDragLeave={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setIsDragging(false);
          }}
          sx={{
            p: 6,
            textAlign: "center",
            cursor: "pointer",
            borderStyle: "dashed",
            borderWidth: 2,
            borderColor: isDragging
              ? theme.palette.primary.main
              : theme.palette.divider,
            backgroundColor: isDragging
              ? `${theme.palette.primary.main}12`
              : "transparent",
            transition: "all 0.15s ease",
            "&:hover": {
              borderColor: isNewTemplate
                ? theme.palette.primary.light
                : theme.palette.secondary.light,
              backgroundColor: isNewTemplate
                ? `${theme.palette.primary.main}05`
                : `${theme.palette.secondary.main}05`,
            },
          }}
        >
          <UploadIcon
            sx={{ fontSize: 64, color: theme.palette.text.secondary, mb: 2 }}
          />
          <Typography variant={"h6"} sx={{ fontWeight: 500, mb: 1 }}>
            {isNewTemplate
              ? "Upload Your New Template Document"
              : "Upload the Template to Modify"}
          </Typography>
          <Typography variant={"body2"} color={"text.secondary"}>
            {isNewTemplate
              ? "Drag and drop a Word document (.docx) here, or click to browse"
              : "Drag and drop a Word document (.docx) or PDF here, or click to browse"}
          </Typography>
          <input
            ref={fileInputRef}
            type={"file"}
            hidden
            accept={isNewTemplate ? ".docx,.doc" : ".docx,.doc,.pdf"}
            onChange={handleFileSelect}
          />
        </Paper>
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ height: "70vh", display: "flex", flexDirection: "column" }}>
        <input
          ref={fileInputRef}
          type={"file"}
          hidden
          accept={isNewTemplate ? ".docx,.doc" : ".docx,.doc,.pdf"}
          onChange={handleFileSelect}
        />
        <Paper
          elevation={0}
          sx={{
            p: 1.5,
            borderBottom: `1px solid ${theme.palette.divider}`,
            display: "flex",
            alignItems: "center",
            gap: 2,
          }}
        >
          <Stack
            direction={"row"}
            alignItems={"center"}
            gap={1}
            sx={{ flex: 1 }}
          >
            <DocIcon color={"primary"} />
            <Typography
              variant={"body2"}
              sx={{
                fontWeight: 500,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {uploadedFile.name}
            </Typography>
            {uploadStatus === "uploading" && (
              <Chip
                icon={
                  <SyncIcon
                    sx={{
                      fontSize: 14,
                      animation: "spin 1s linear infinite",
                      "@keyframes spin": {
                        from: { transform: "rotate(0deg)" },
                        to: { transform: "rotate(360deg)" },
                      },
                    }}
                  />
                }
                label={"Uploading..."}
                size={"small"}
                color={"info"}
              />
            )}
            {uploadStatus === "uploaded" && (
              <Chip
                icon={<CloudDoneIcon sx={{ fontSize: 14 }} />}
                label={"Saved to cloud"}
                size={"small"}
                color={"success"}
              />
            )}
            {uploadStatus === "error" && (
              <Chip
                icon={<ErrorIcon sx={{ fontSize: 14 }} />}
                label={"Upload failed"}
                size={"small"}
                color={"error"}
              />
            )}
            <Chip
              label={`${placements.length} field(s) placed`}
              size={"small"}
              color={placements.length > 0 ? "success" : "default"}
            />
            {annotations.length > 0 && (
              <Chip
                icon={<DrawIcon sx={{ fontSize: 14 }} />}
                label={`${annotations.length} annotation(s)`}
                size={"small"}
                color={"primary"}
                variant={"outlined"}
              />
            )}
          </Stack>

          <Stack direction={"row"} alignItems={"center"} gap={1}>
            <Tooltip title={"Zoom out"}>
              <IconButton size={"small"} onClick={handleZoomOut}>
                <ZoomOutIcon />
              </IconButton>
            </Tooltip>
            <Typography
              variant={"caption"}
              sx={{ minWidth: 40, textAlign: "center" }}
            >
              {Math.round(zoom * 100)}
              {"%"}
            </Typography>
            <Tooltip title={"Zoom in"}>
              <IconButton size={"small"} onClick={handleZoomIn}>
                <ZoomInIcon />
              </IconButton>
            </Tooltip>
            <Divider orientation={"vertical"} flexItem sx={{ mx: 1 }} />
            {originalS3FileId && (
              <Button
                size={"small"}
                variant={"outlined"}
                startIcon={<DownloadIcon />}
                onClick={handleDownloadOriginal}
                isLoading={downloadingOriginal}
              >
                {"Download Original"}
              </Button>
            )}
            <Button
              size={"small"}
              variant={"outlined"}
              startIcon={<ReplaceIcon />}
              onClick={() => fileInputRef.current?.click()}
            >
              {"Replace template file"}
            </Button>
          </Stack>
        </Paper>

        <Box sx={{ flex: 1, display: "flex", overflow: "hidden" }}>
          <VariablePalette
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            placedVariables={placedVariables}
            onRequestNewVariable={() => setNewVariableDialogOpen(true)}
            customVariables={requestedVariables}
          />

          <DocumentPreviewCanvas
            uploadedFile={uploadedFile}
            s3FileId={s3FileId}
            useServerSidePdfPreview={useServerSidePdfPreview}
            convertFileToPdfFn={convertFileToPdfFn}
            placements={placements}
            onAddPlacement={handleAddPlacement}
            onRemovePlacement={handleRemovePlacement}
            onMovePlacement={handleMovePlacement}
            onEditCondition={handleEditCondition}
            zoom={zoom}
            annotations={annotations}
            onAddAnnotation={handleAddAnnotation}
            onRemoveAnnotation={handleRemoveAnnotation}
            onMoveAnnotation={handleMoveAnnotation}
            onResizeAnnotation={handleResizeAnnotation}
            onRequestComment={handleRequestComment}
          />

          <AnnotationToolbar annotations={annotations} />
        </Box>
      </Box>

      {canSubmit && (
        <Paper
          elevation={0}
          sx={{
            p: 2,
            mt: 2,
            borderTop: `1px solid ${theme.palette.divider}`,
            backgroundColor: `${theme.palette.success.main}05`,
          }}
        >
          {(placements.length > 0 || annotations.length > 0) && (
            <Box sx={{ mb: 2 }}>
              {placements.length > 0 && (
                <Box sx={{ mb: annotations.length > 0 ? 2 : 0 }}>
                  <Typography
                    variant={"subtitle2"}
                    sx={{ fontWeight: 600, mb: 0.5, color: "success.main" }}
                  >
                    {"Placed Fields"} {"("}
                    {placements.length}
                    {")"}
                  </Typography>
                  <Typography
                    variant={"caption"}
                    color={"text.secondary"}
                    sx={{ display: "block", mb: 1 }}
                  >
                    {
                      "Click on a field to set when it should display (e.g., only if a condition is met)"
                    }
                  </Typography>
                  <Stack direction={"row"} flexWrap={"wrap"} gap={1}>
                    {placements.map((placement) => (
                      <Tooltip
                        key={placement.id}
                        title={
                          placement.condition
                            ? `Show if: ${placement.condition}`
                            : "Click the field on the document to add a condition"
                        }
                      >
                        <Chip
                          label={
                            placement.condition
                              ? `${placement.variable.label} (conditional)`
                              : placement.variable.label
                          }
                          size={"small"}
                          onClick={() => handleEditCondition(placement)}
                          onDelete={() => handleRemovePlacement(placement.id)}
                          color={
                            placement.variable.isCustom
                              ? "warning"
                              : placement.condition
                              ? "info"
                              : "success"
                          }
                          variant={"outlined"}
                          sx={{
                            cursor: "pointer",
                            "& .MuiChip-label": {
                              fontStyle: placement.condition
                                ? "italic"
                                : "normal",
                            },
                          }}
                        />
                      </Tooltip>
                    ))}
                  </Stack>
                </Box>
              )}
              {annotations.length > 0 && (
                <Box>
                  <Typography
                    variant={"subtitle2"}
                    sx={{ fontWeight: 600, mb: 1, color: "primary.main" }}
                  >
                    {"Annotations"} {"("}
                    {annotations.length}
                    {")"}
                  </Typography>
                  <Stack direction={"row"} flexWrap={"wrap"} gap={1}>
                    {annotations.map((annotation) => {
                      const tool = ANNOTATION_TOOLS.find(
                        (t) => t.id === annotation.type
                      );
                      return (
                        <Chip
                          key={annotation.id}
                          label={
                            annotation.type === "comment"
                              ? annotation.text?.substring(0, 20) +
                                (annotation.text?.length > 20 ? "..." : "")
                              : tool?.label
                          }
                          size={"small"}
                          onDelete={() => handleRemoveAnnotation(annotation.id)}
                          sx={{ borderColor: tool?.color, color: tool?.color }}
                          variant={"outlined"}
                        />
                      );
                    })}
                  </Stack>
                </Box>
              )}
            </Box>
          )}
          {(placements.length > 0 || annotations.length > 0) && (
            <Divider sx={{ mb: 2 }} />
          )}
          <Stack direction={"column"} gap={2} sx={{ mb: 2 }}>
            <TextField
              fullWidth
              label={"Template Name"}
              placeholder={uploadedFile?.name ?? ""}
              value={requestTitle}
              onChange={(e) => setRequestTitle(e.target.value)}
              helperText={"What this template should be called once published"}
            />
            <TextField
              fullWidth
              multiline
              minRows={3}
              label={"Any additional comments"}
              placeholder={
                isNewTemplate
                  ? "Anything else we should know about this new template..."
                  : "Anything else we should know about the changes..."
              }
              value={requestDescription}
              onChange={(e) => setRequestDescription(e.target.value)}
            />
            <TextField
              fullWidth
              select
              label={"Priority"}
              value={requestPriority}
              onChange={(e) => setRequestPriority(e.target.value)}
              SelectProps={{ native: true }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position={"start"}>
                    <PriorityIcon
                      sx={{
                        color:
                          requestPriority === "HIGH"
                            ? "error.main"
                            : requestPriority === "MEDIUM"
                            ? "warning.main"
                            : "success.main",
                      }}
                    />
                  </InputAdornment>
                ),
              }}
            >
              <option value={"LOW"}>
                {"Low - No rush, whenever convenient"}
              </option>
              <option value={"MEDIUM"}>{"Medium - Standard turnaround"}</option>
              <option value={"HIGH"}>{"High - Urgent, needed soon"}</option>
            </TextField>
          </Stack>
          <Stack direction={"row"} justifyContent={"flex-end"}>
            <Tooltip
              title={
                uploadStatus === "uploading"
                  ? "Please wait for file upload to complete"
                  : uploadStatus === "error"
                  ? "File upload failed - please re-upload the document"
                  : ""
              }
            >
              <span>
                <Button
                  size={"large"}
                  startIcon={<SubmitIcon />}
                  onClick={handleInlineSubmit}
                  disabled={
                    uploadStatus === "uploading" || uploadStatus === "error"
                  }
                  sx={{
                    minWidth: 180,
                    py: 1.5,
                    backgroundColor: theme.palette.primary.main,
                    color: "white",
                    "&:hover": { backgroundColor: theme.palette.primary.dark },
                    "&.Mui-disabled": {
                      backgroundColor: theme.palette.grey[400],
                      color: theme.palette.grey[600],
                    },
                  }}
                >
                  {submitLabel || "Submit Request"}
                </Button>
              </span>
            </Tooltip>
          </Stack>
        </Paper>
      )}

      <NewVariableDialog
        open={newVariableDialogOpen}
        onClose={() => setNewVariableDialogOpen(false)}
        onSubmit={handleRequestNewVariable}
      />

      <CommentDialog
        open={commentDialogOpen}
        onClose={() => {
          setCommentDialogOpen(false);
          setPendingCommentPosition(null);
        }}
        onSubmit={handleAddComment}
      />

      <ConditionDialog
        open={conditionDialogOpen}
        onClose={() => {
          setConditionDialogOpen(false);
          setEditingPlacement(null);
        }}
        onSubmit={handleUpdateCondition}
        placement={editingPlacement}
      />
    </>
  );
};

export default DocumentVariableMapper;
